import React, { useState, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { X, Search, Wrench, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

/**
 * Get language for Monaco editor based on file extension
 */
const getLanguage = (filename) => {
    if (!filename) return 'plaintext';
    const ext = filename.split('.').pop()?.toLowerCase();
    const languageMap = {
        'js': 'javascript',
        'jsx': 'javascript',
        'ts': 'typescript',
        'tsx': 'typescript',
        'html': 'html',
        'css': 'css',
        'json': 'json',
        'md': 'markdown',
        'py': 'python',
        'sql': 'sql',
        'sh': 'shell',
        'yml': 'yaml',
        'yaml': 'yaml',
        'xml': 'xml'
    };
    return languageMap[ext] || 'plaintext';
};

/**
 * Code editor panel with Monaco editor
 */
export const CodeEditorPanel = ({
    activeFile,
    openFiles,
    fileContents,
    unsavedChanges,
    onFileSelect,
    onCloseFile,
    onCodeChange
}) => {
    const [showSearch, setShowSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchMatches, setSearchMatches] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const editorRef = useRef(null);
    const monacoRef = useRef(null);
    const decorationsRef = useRef([]);

    // Handle editor mount
    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;
        monacoRef.current = monaco;
    };

    // Perform search in the file
    const performSearch = useCallback((text) => {
        if (!editorRef.current || !monacoRef.current || !text) {
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
            // Clear decorations
            if (editorRef.current) {
                decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
            }
            return;
        }

        const model = editorRef.current.getModel();
        if (!model) return;

        const matches = model.findMatches(text, true, false, false, null, true);
        setSearchMatches(matches);
        
        if (matches.length > 0) {
            setCurrentMatchIndex(0);
            // Highlight all matches
            const decorations = matches.map((match, index) => ({
                range: match.range,
                options: {
                    className: index === 0 ? 'current-search-match' : 'search-match',
                    inlineClassName: index === 0 ? 'current-search-match-inline' : 'search-match-inline'
                }
            }));
            decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, decorations);
            
            // Go to first match
            editorRef.current.revealLineInCenter(matches[0].range.startLineNumber);
            editorRef.current.setPosition({
                lineNumber: matches[0].range.startLineNumber,
                column: matches[0].range.startColumn
            });
        } else {
            setCurrentMatchIndex(-1);
            decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
        }
    }, []);

    // Find next match
    const findNext = useCallback(() => {
        if (searchMatches.length === 0) return;
        
        const nextIndex = (currentMatchIndex + 1) % searchMatches.length;
        setCurrentMatchIndex(nextIndex);
        
        const match = searchMatches[nextIndex];
        if (match && editorRef.current) {
            // Update decorations to highlight current match
            const decorations = searchMatches.map((m, index) => ({
                range: m.range,
                options: {
                    className: index === nextIndex ? 'current-search-match' : 'search-match',
                    inlineClassName: index === nextIndex ? 'current-search-match-inline' : 'search-match-inline'
                }
            }));
            decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, decorations);
            
            editorRef.current.revealLineInCenter(match.range.startLineNumber);
            editorRef.current.setPosition({
                lineNumber: match.range.startLineNumber,
                column: match.range.startColumn
            });
        }
    }, [searchMatches, currentMatchIndex]);

    // Find previous match
    const findPrevious = useCallback(() => {
        if (searchMatches.length === 0) return;
        
        const prevIndex = currentMatchIndex <= 0 ? searchMatches.length - 1 : currentMatchIndex - 1;
        setCurrentMatchIndex(prevIndex);
        
        const match = searchMatches[prevIndex];
        if (match && editorRef.current) {
            // Update decorations to highlight current match
            const decorations = searchMatches.map((m, index) => ({
                range: m.range,
                options: {
                    className: index === prevIndex ? 'current-search-match' : 'search-match',
                    inlineClassName: index === prevIndex ? 'current-search-match-inline' : 'search-match-inline'
                }
            }));
            decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, decorations);
            
            editorRef.current.revealLineInCenter(match.range.startLineNumber);
            editorRef.current.setPosition({
                lineNumber: match.range.startLineNumber,
                column: match.range.startColumn
            });
        }
    }, [searchMatches, currentMatchIndex]);

    // Handle search input change
    const handleSearchChange = (e) => {
        const text = e.target.value;
        setSearchText(text);
        performSearch(text);
    };

    // Handle search key press (Enter for next, Shift+Enter for previous)
    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) {
                findPrevious();
            } else {
                findNext();
            }
        } else if (e.key === 'Escape') {
            setShowSearch(false);
            setSearchText('');
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
            if (editorRef.current) {
                decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
                editorRef.current.focus();
            }
        }
    };

    // Toggle search panel
    const toggleSearch = () => {
        setShowSearch(!showSearch);
        if (!showSearch) {
            // Clear previous search when opening
            setSearchText('');
            setSearchMatches([]);
            setCurrentMatchIndex(-1);
        } else {
            // Clear decorations when closing
            if (editorRef.current) {
                decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
            }
        }
    };

    return (
        <div className="h-full flex flex-col bg-white">
            {/* Toolbar and File tabs */}
            <div className="flex items-center gap-1 p-1 bg-gray-100 border-b border-gray-200">
                {/* Toolbar buttons */}
                <TooltipProvider>
                    <div className="flex items-center gap-1 pr-2 border-r border-gray-300 mr-1">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant={showSearch ? "secondary" : "ghost"}
                                    size="sm"
                                    onClick={toggleSearch}
                                    className="h-7 w-7 p-0"
                                    data-testid="search-btn"
                                >
                                    <Search className="w-4 h-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Caută</p>
                            </TooltipContent>
                        </Tooltip>
                        
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    data-testid="debug-btn"
                                >
                                    <Wrench className="w-4 h-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Debug</p>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </TooltipProvider>

                {/* File tabs */}
                <div className="flex items-center gap-1 overflow-x-auto flex-1">
                    {openFiles.map((file) => (
                        <div
                            key={file}
                            onClick={() => onFileSelect(file)}
                            className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded cursor-pointer transition-colors ${
                                activeFile === file
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <span className="truncate max-w-[120px]">{file}</span>
                            {unsavedChanges[file] && (
                                <span className="w-2 h-2 rounded-full bg-orange-400" title="Unsaved changes" />
                            )}
                            <button
                                onClick={(e) => onCloseFile(file, e)}
                                className="ml-1 p-0.5 hover:bg-gray-300 rounded"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Search panel */}
            {showSearch && (
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                    <Search className="w-4 h-4 text-gray-400" />
                    <Input
                        type="text"
                        placeholder="Caută în fișier..."
                        value={searchText}
                        onChange={handleSearchChange}
                        onKeyDown={handleSearchKeyDown}
                        className="h-7 text-sm flex-1 max-w-xs"
                        autoFocus
                        data-testid="search-input"
                    />
                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={findPrevious}
                            disabled={searchMatches.length === 0}
                            className="h-7 w-7 p-0"
                            title="Rezultatul anterior (Shift+Enter)"
                        >
                            <ChevronUp className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={findNext}
                            disabled={searchMatches.length === 0}
                            className="h-7 w-7 p-0"
                            title="Rezultatul următor (Enter)"
                        >
                            <ChevronDown className="w-4 h-4" />
                        </Button>
                    </div>
                    {searchText && (
                        <span className="text-xs text-gray-500">
                            {searchMatches.length > 0 
                                ? `${currentMatchIndex + 1} / ${searchMatches.length}`
                                : 'Niciun rezultat'
                            }
                        </span>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                            setShowSearch(false);
                            setSearchText('');
                            setSearchMatches([]);
                            if (editorRef.current) {
                                decorationsRef.current = editorRef.current.deltaDecorations(decorationsRef.current, []);
                            }
                        }}
                        className="h-7 w-7 p-0"
                    >
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            )}

            {/* Monaco Editor */}
            <div className="flex-1">
                {activeFile ? (
                    <Editor
                        height="100%"
                        language={getLanguage(activeFile)}
                        value={fileContents[activeFile] || ''}
                        onChange={onCodeChange}
                        onMount={handleEditorDidMount}
                        theme="vs-light"
                        options={{
                            minimap: { enabled: false },
                            fontSize: 13,
                            lineNumbers: 'on',
                            scrollBeyondLastLine: false,
                            automaticLayout: true,
                            tabSize: 2,
                            wordWrap: 'on',
                            padding: { top: 10 }
                        }}
                    />
                ) : (
                    <div className="h-full flex items-center justify-center text-gray-400">
                        <p>Select a file to edit</p>
                    </div>
                )}
            </div>

            {/* CSS for search highlighting */}
            <style>{`
                .search-match {
                    background-color: rgba(255, 235, 59, 0.4);
                }
                .search-match-inline {
                    background-color: rgba(255, 235, 59, 0.4);
                }
                .current-search-match {
                    background-color: rgba(255, 152, 0, 0.6);
                }
                .current-search-match-inline {
                    background-color: rgba(255, 152, 0, 0.6);
                }
            `}</style>
        </div>
    );
};

export default CodeEditorPanel;
